/**
 * Multi-league in-week dashboard: discover Sleeper leagues, load matchups,
 * merge rooting exposures, and fill pre-kickoff gaps with ESPN projections.
 */

import { fetchSleeperUserId } from './draftBoard.js';
import { getEspnProjectionsCached, indexEspnPlayers, scoreProjection } from './espnProjections.js';
import { helpers } from './rootingScores.js';

const SLEEPER = 'https://api.sleeper.app/v1';
const SLEEPER_SCHEDULE = 'https://api.sleeper.com/schedule/nfl';
const NFL_REGULAR_WEEKS = 17;

const GAME_COMPLETE_STATUSES = new Set([
  'complete',
  'closed',
  'final',
  'finished',
  'post',
  'post_game',
  'postgame',
]);
const GAME_STARTED_STATUSES = new Set([
  'in_progress',
  'inprogress',
  'in_game',
  'ingame',
  'live',
  'halftime',
  'started',
  ...GAME_COMPLETE_STATUSES,
]);

const normalizeGameStatus = (status) =>
  String(status || '')
    .trim()
    .toLowerCase()
    .replace(/-/g, '_');

const isGameCompleteStatus = (status) => GAME_COMPLETE_STATUSES.has(normalizeGameStatus(status));

const isGameStartedStatus = (status) => GAME_STARTED_STATUSES.has(normalizeGameStatus(status));

/** Eastern calendar date (YYYY-MM-DD) for kickoff comparisons. */
const easternToday = () =>
  new Intl.DateTimeFormat('en-CA', { timeZone: 'America/New_York' }).format(new Date());

/** True when the schedule game date is strictly before today (Eastern). */
const kickoffDatePassed = (gameDate) => {
  if (!gameDate) return false;
  return String(gameDate) < easternToday();
};


const fetchJson = async (url) => {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Sleeper request failed (${response.status}).`);
  }
  return response.json();
};

let nflStateCache = null;
let nflStateFetchedAt = 0;

/** Current NFL week/season from Sleeper. Cached briefly to avoid hammering /state. */
export const fetchNflState = async () => {
  const now = Date.now();
  if (nflStateCache && now - nflStateFetchedAt < 60_000) {
    return nflStateCache;
  }
  nflStateCache = await fetchJson(`${SLEEPER}/state/nfl`);
  nflStateFetchedAt = now;
  return nflStateCache;
};

let sleeperPlayersCache = null;
let nflScheduleCache = new Map();

export const getSleeperPlayers = async () => {
  if (sleeperPlayersCache) return sleeperPlayersCache;
  sleeperPlayersCache = await fetchJson(`${SLEEPER}/players/nfl`);
  return sleeperPlayersCache;
};

/** NFL regular-season schedule from Sleeper (kickoff date + game status per team/week). */
export const fetchNflSchedule = async (season) => {
  const key = String(season);
  if (nflScheduleCache.has(key)) return nflScheduleCache.get(key);

  const urls = [
    `${SLEEPER_SCHEDULE}/regular/${key}`,
    `${SLEEPER}/schedule/nfl/regular/${key}`,
  ];

  for (const url of urls) {
    try {
      const response = await fetch(url);
      if (!response.ok) continue;
      const games = await response.json();
      if (Array.isArray(games) && games.length) {
        nflScheduleCache.set(key, games);
        return games;
      }
    } catch {
      // try next host
    }
  }

  nflScheduleCache.set(key, []);
  return [];
};

/** team_abbr + week → schedule row (home/away, status, date). */
export const buildScheduleIndex = (games) => {
  const index = new Map();
  (games || []).forEach((game) => {
    if (!game?.week) return;
    index.set(`${game.home}-${game.week}`, game);
    index.set(`${game.away}-${game.week}`, game);
  });
  return index;
};

const playerTeamAbbr = (playerId, meta) => {
  if (meta?.position === 'DEF' || (typeof playerId === 'string' && playerId.length <= 3)) {
    return typeof playerId === 'string' ? playerId : meta?.team;
  }
  return meta?.team;
};

const numericPoints = (value) => (typeof value === 'number' && !Number.isNaN(value) ? value : null);

const teamHasLiveScoring = (teamAbbr, entry, playersDb) => {
  if (!teamAbbr || !entry) return false;

  const rosterPlayers = entry.players || entry.starters || [];
  const pointsMap = entry.players_points || {};

  const teamHasScoringPlayer = rosterPlayers.some((playerId) => {
    const pts = numericPoints(pointsMap[playerId]);
    if (pts == null || pts === 0) return false;
    const meta = playerMeta(playerId, playersDb);
    return playerTeamAbbr(playerId, meta) === teamAbbr;
  });
  if (teamHasScoringPlayer) return true;

  const starterIndexById = new Map(
    (entry.starters || []).map((playerId, index) => [playerId, index])
  );
  return rosterPlayers.some((playerId) => {
    const idx = starterIndexById.get(playerId);
    const starterPts =
      idx != null && Array.isArray(entry.starters_points)
        ? numericPoints(entry.starters_points[idx])
        : null;
    if (starterPts == null || starterPts === 0) return false;
    const meta = playerMeta(playerId, playersDb);
    return playerTeamAbbr(playerId, meta) === teamAbbr;
  });
};

const starterSlotPoints = (playerId, entry) => {
  const starterIndex = entry?.starters?.indexOf(playerId) ?? -1;
  if (starterIndex < 0 || !Array.isArray(entry?.starters_points)) return null;
  return numericPoints(entry.starters_points[starterIndex]);
};

/**
 * Derive played/locked from Sleeper schedule status plus matchup scoring cues.
 * - Locked: game started, kickoff passed, or team already has live fantasy points.
 * - Played: game complete, or player has recorded fantasy points (including 0).
 */
export const resolvePlayerPlayState = ({
  playerId,
  meta,
  entry,
  week,
  scheduleIndex,
  playersDb,
}) => {
  const teamAbbr = playerTeamAbbr(playerId, meta);
  const game = teamAbbr && scheduleIndex ? scheduleIndex.get(`${teamAbbr}-${week}`) : null;
  const gameStatus = game?.status || null;
  const normalizedStatus = normalizeGameStatus(gameStatus);

  const livePts = numericPoints(entry?.players_points?.[playerId]);
  const hasRecordedScore = livePts != null && livePts !== 0;
  const teamScoring = teamHasLiveScoring(teamAbbr, entry, playersDb);

  const gameComplete =
    isGameCompleteStatus(gameStatus) ||
    (kickoffDatePassed(game?.date) &&
      normalizedStatus !== 'canceled' &&
      normalizedStatus !== 'pre_game');
  const gameStarted =
    isGameStartedStatus(gameStatus) ||
    teamScoring ||
    (kickoffDatePassed(game?.date) && normalizedStatus !== 'canceled');

  // Scoring proves a game kicked off, never that it ended. Reading a non-zero
  // score as "played" marked every player in a live game Final — and, because
  // the projection treats a finished player as having nothing left to give, it
  // also dropped his remaining points out of the projected final.
  const hasPlayed = gameComplete;
  const isLocked = gameComplete || gameStarted || hasRecordedScore;

  let playState = 'open';
  if (hasPlayed) playState = 'played';
  else if (isLocked) playState = 'locked';

  return {
    isLocked,
    hasPlayed,
    playState,
    gameStatus,
    gameDate: game?.date || null,
  };
};

const INJURY_ALERTS = new Set(['Out', 'IR', 'Doubtful', 'Questionable', 'PUP', 'Sus']);
const SEVERE_INJURY = new Set(['Out', 'IR', 'PUP', 'Sus']);
const DOUBTFUL_INJURY = new Set(['Doubtful']);
const QUESTIONABLE_INJURY = new Set(['Questionable']);
const ACTIONABLE_INJURY = new Set([...SEVERE_INJURY, ...DOUBTFUL_INJURY, ...QUESTIONABLE_INJURY]);

/** Sleeper injury_status is usually Title Case; normalize so move detection stays stable. */
const normalizeInjuryStatus = (status) => {
  const raw = String(status || '').trim();
  if (!raw) return null;
  const lower = raw.toLowerCase();
  if (lower === 'out') return 'Out';
  if (lower === 'ir') return 'IR';
  if (lower === 'pup') return 'PUP';
  if (lower === 'doubtful') return 'Doubtful';
  if (lower === 'questionable' || lower === 'q') return 'Questionable';
  if (lower === 'sus' || lower === 'suspended') return 'Sus';
  return raw;
};
const STARTER_SLOTS = new Set(['QB', 'RB', 'WR', 'TE', 'FLEX', 'SUPER_FLEX', 'REC_FLEX', 'K', 'DEF']);
const FLEX_SLOTS = new Set(['FLEX', 'SUPER_FLEX', 'REC_FLEX']);

const SLOT_ELIGIBILITY = {
  QB: ['QB'],
  RB: ['RB'],
  WR: ['WR'],
  TE: ['TE'],
  FLEX: ['RB', 'WR', 'TE'],
  SUPER_FLEX: ['QB', 'RB', 'WR', 'TE'],
  REC_FLEX: ['RB', 'WR', 'TE'],
  K: ['K'],
  DEF: ['DEF'],
};

export const isInjuryAlert = (status) => INJURY_ALERTS.has(status);

const canFillSlot = (playerPosition, slot) => {
  const eligible = SLOT_ELIGIBILITY[slot] || [slot];
  return eligible.includes(playerPosition);
};

const starterSlotTypes = (rosterPositions) =>
  (rosterPositions || []).filter((slot) => STARTER_SLOTS.has(slot));

const formatPlayerName = (player) =>
  player?.full_name ||
  [player?.first_name, player?.last_name].filter(Boolean).join(' ') ||
  'Unknown';

export const playerMeta = (playerId, playersDb) => {
  const player = playersDb[playerId];
  if (!player) {
    const isDefense = typeof playerId === 'string' && playerId.length <= 3;
    return {
      id: playerId,
      name: isDefense ? playerId : `Player ${playerId}`,
      position: isDefense ? 'DEF' : 'FLEX',
      team: isDefense ? playerId : 'FA',
      injuryStatus: null,
      gameStatus: null,
    };
  }
  return {
    id: playerId,
    name: formatPlayerName(player),
    position: player.position || player.fantasy_positions?.[0] || 'FLEX',
    team: player.team || player.metadata?.team_abbr || 'FA',
    injuryStatus: normalizeInjuryStatus(player.injury_status),
    gameStatus: player.status || null,
  };
};

const teamLabel = (user, rosterId) =>
  user?.metadata?.team_name?.trim() ||
  user?.display_name?.trim() ||
  user?.username?.trim() ||
  `Team ${rosterId}`;

/** All NFL leagues for a user in a season. */
export const discoverUserLeagues = async (username, season) => {
  const userId = await fetchSleeperUserId(username);
  if (!userId) {
    throw new Error(`Could not find Sleeper user "${username}".`);
  }
  const leagues = await fetchJson(`${SLEEPER}/user/${userId}/leagues/nfl/${season}`);
  return { userId, leagues: Array.isArray(leagues) ? leagues : [] };
};

const matchupHasLiveScoring = (matchupEntries) =>
  matchupEntries.some((entry) => {
    const points = entry?.players_points || {};
    return Object.values(points).some((v) => typeof v === 'number' && v > 0);
  });

export const buildProjectionLookup = async () => {
  const payload = await getEspnProjectionsCached();
  const lookup = indexEspnPlayers(payload.players);
  const weeklyById = new Map();

  const weeklyFor = (meta) => {
    const hit = lookup(meta);
    if (!hit) return null;
    const seasonTotal = scoreProjection(hit.stats) ?? hit.espnPprTotal;
    if (seasonTotal == null) return null;
    return Math.round((seasonTotal / NFL_REGULAR_WEEKS) * 10) / 10;
  };

  return { lookup, weeklyFor };
};

/**
 * Resolve starter fantasy points: live when the matchup has scoring activity,
 * otherwise ESPN weekly projection.
 */
const resolveStarterPoints = (playerId, entry, weeklyFor, meta) => {
  const live = entry?.players_points?.[playerId];
  // Prefer live only when the player has actually scored; zero often means not kicked off yet.
  if (typeof live === 'number' && live > 0) {
    return { points: live, source: 'live' };
  }
  const projected = weeklyFor(meta);
  if (projected != null) {
    return { points: projected, source: 'projection' };
  }
  if (typeof live === 'number') {
    return { points: live, source: 'live' };
  }
  return { points: 0, source: 'none' };
};

const buildPlayerRow = (
  playerId,
  entry,
  weeklyFor,
  playersDb,
  { isStarter = false, week, scheduleIndex } = {}
) => {
  const meta = playerMeta(playerId, playersDb);
  const { points, source } = resolveStarterPoints(playerId, entry, weeklyFor, meta);
  const starterIndex = entry?.starters?.indexOf(playerId) ?? -1;
  const liveStarterPts =
    starterIndex >= 0 && Array.isArray(entry?.starters_points)
      ? entry.starters_points[starterIndex]
      : null;
  const playState = resolvePlayerPlayState({
    playerId,
    meta,
    entry,
    week,
    scheduleIndex,
    playersDb,
  });

  return {
    ...meta,
    points,
    pointSource: source,
    livePoints: typeof liveStarterPts === 'number' ? liveStarterPts : null,
    isStarter,
    injuryAlert: isInjuryAlert(meta.injuryStatus),
    ...playState,
  };
};

const buildStarterRow = (playerId, entry, weeklyFor, playersDb, context) =>
  buildPlayerRow(playerId, entry, weeklyFor, playersDb, { isStarter: true, ...context });

/** One league's week snapshot for the signed-in user. */
export const fetchLeagueWeekSnapshot = async ({
  leagueId,
  week,
  userId,
  playersDb,
  weeklyFor,
  scheduleIndex,
}) => {
  const [league, users, rosters, matchups] = await Promise.all([
    fetchJson(`${SLEEPER}/league/${leagueId}`),
    fetchJson(`${SLEEPER}/league/${leagueId}/users`),
    fetchJson(`${SLEEPER}/league/${leagueId}/rosters`),
    fetchJson(`${SLEEPER}/league/${leagueId}/matchups/${week}`),
  ]);

  const myRoster = rosters.find((r) => r.owner_id === userId);
  if (!myRoster) {
    return null;
  }

  const myMatchup = matchups.find((m) => m.roster_id === myRoster.roster_id);
  if (!myMatchup?.matchup_id) {
    return null;
  }

  const opponentEntry = matchups.find(
    (m) => m.matchup_id === myMatchup.matchup_id && m.roster_id !== myRoster.roster_id
  );

  const opponentRoster = rosters.find((r) => r.roster_id === opponentEntry?.roster_id);
  const myUser = users.find((u) => u.user_id === userId);
  const oppUser = users.find((u) => u.user_id === opponentRoster?.owner_id);

  const liveScoring = matchupHasLiveScoring([myMatchup, opponentEntry].filter(Boolean));
  const rowContext = { week, scheduleIndex };

  const starterIds = new Set(
    (myMatchup.starters || []).filter((id) => id && id !== '0')
  );

  const myStarters = (myMatchup.starters || [])
    .filter((id) => id && id !== '0')
    .map((id) => buildStarterRow(id, myMatchup, weeklyFor, playersDb, rowContext));

  const myBench = (myRoster.players || [])
    .filter((id) => id && id !== '0' && !starterIds.has(id))
    .map((id) =>
      buildPlayerRow(id, myMatchup, weeklyFor, playersDb, { isStarter: false, ...rowContext })
    );

  const oppStarters = (opponentEntry?.starters || [])
    .filter((id) => id && id !== '0')
    .map((id) => buildStarterRow(id, opponentEntry, weeklyFor, playersDb, rowContext));

  const injuryAlerts = [...myStarters, ...oppStarters].filter((s) => s.injuryAlert);

  return {
    leagueId,
    leagueName: league.name || `League ${leagueId}`,
    week,
    rosterPositions: league.roster_positions || [],
    scoringSettings: league.scoring_settings || {},
    myTeam: {
      rosterId: myRoster.roster_id,
      name: teamLabel(myUser, myRoster.roster_id),
      points: myMatchup.points ?? 0,
      starters: myStarters,
      bench: myBench,
    },
    opponent: {
      rosterId: opponentEntry?.roster_id,
      name: teamLabel(oppUser, opponentEntry?.roster_id),
      points: opponentEntry?.points ?? 0,
      starters: oppStarters,
    },
    liveScoring,
    injuryAlerts,
  };
};

const addExposure = (map, playerId, meta, leagueLabel, side, points) => {
  if (!map.has(playerId)) {
    map.set(playerId, {
      id: playerId,
      name: meta.name,
      position: meta.position,
      team: meta.team,
      injuryStatus: meta.injuryStatus,
      support: 0,
      against: 0,
      leaguesFor: [],
      leaguesAgainst: [],
    });
  }
  const record = map.get(playerId);
  if (side === 'for') {
    record.support += points;
    record.leaguesFor.push({ league: leagueLabel, points });
  } else {
    record.against += points;
    record.leaguesAgainst.push({ league: leagueLabel, points });
  }
  if (meta.injuryStatus) {
    record.injuryStatus = meta.injuryStatus;
  }
};

/** Merge rooting exposures across league snapshots. */
export const buildMergedRootingBoard = (leagueSnapshots) => {
  const exposures = new Map();

  leagueSnapshots.forEach((snap) => {
    if (!snap) return;
    const label = snap.leagueName;

    snap.myTeam.starters.forEach((starter) => {
      addExposure(exposures, starter.id, starter, label, 'for', starter.points);
    });
    snap.opponent.starters.forEach((starter) => {
      addExposure(exposures, starter.id, starter, label, 'against', starter.points);
    });
  });

  const players = Array.from(exposures.values()).map((record) => {
    const net = record.support - record.against;
    const isConflict =
      record.leaguesFor.length > 0 && record.leaguesAgainst.length > 0;
    const multiLeague =
      record.leaguesFor.length + record.leaguesAgainst.length > 1;

    return {
      ...record,
      net,
      importance: Math.abs(net),
      isConflict,
      multiLeague,
      injuryAlert: isInjuryAlert(record.injuryStatus),
      displaySupport: helpers.formatNumber(record.support),
      displayAgainst: helpers.formatNumber(record.against),
      displayNet: helpers.formatNumber(net),
      tags: {
        rootingFor: record.support > 0,
        rootingAgainst: record.against > 0,
        conflict: isConflict,
      },
    };
  });

  players.sort((a, b) => {
    if (a.isConflict !== b.isConflict) return a.isConflict ? -1 : 1;
    return b.importance - a.importance;
  });

  const totals = players.reduce(
    (acc, p) => {
      acc.support += p.support;
      acc.against += p.against;
      acc.net += p.net;
      return acc;
    },
    { support: 0, against: 0, net: 0 }
  );

  const conflicts = players.filter((p) => p.isConflict);

  return { players, totals, conflicts };
};

const formatMovePoints = (value) => {
  const numeric = Number(value || 0);
  if (numeric === 0) return '0';
  const rounded = Math.round(numeric * 10) / 10;
  return rounded > 0 ? `~${rounded}` : String(rounded);
};

/**
 * The identity a lineup-fix card needs to draw one of its two players.
 *
 * A move used to be a sentence and a couple of ids. The sentence is still the
 * instruction, but the card also shows who is coming out and who is going in,
 * and an avatar needs a team and a position to fall back on — so the move
 * carries the pair rather than making the component search the snapshot for
 * players it already had in hand.
 */
const movePlayer = (player) => ({
  id: player.id,
  name: player.name,
  position: player.position,
  team: player.team,
});

const findBestBenchForSlot = (bench, slot, excludeIds = new Set()) => {
  const eligible = bench.filter(
    (player) => !excludeIds.has(player.id) && canFillSlot(player.position, slot)
  );
  if (!eligible.length) return null;
  return eligible.reduce((best, player) => (player.points > best.points ? player : best));
};

/**
 * Concrete start/sit and flex moves across loaded leagues.
 * Prioritizes injured starters, then clear projection upgrades.
 */
export const buildRosterMoves = (leagueSnapshots, limit = 5) => {
  const moves = [];

  leagueSnapshots.forEach((snap) => {
    if (!snap?.myTeam) return;

    const { myTeam, leagueName, rosterPositions } = snap;
    const slots = starterSlotTypes(rosterPositions);
    const usedBenchIds = new Set();

    myTeam.starters.forEach((starter, index) => {
      const slot = slots[index] || starter.position || 'FLEX';
      const movableBench = (myTeam.bench || []).filter(
        (p) => !usedBenchIds.has(p.id) && !p.isLocked
      );
      const injuryStatus = starter.injuryStatus;
      const isSevere = SEVERE_INJURY.has(injuryStatus);
      const isDoubtful = DOUBTFUL_INJURY.has(injuryStatus);

      // Injury sit→start is still the fold decision when a starter is Q/Out/Doubtful,
      // even if that player's game has kicked off. Locked only gates projection upgrades.
      if (ACTIONABLE_INJURY.has(injuryStatus)) {
        const replacement = findBestBenchForSlot(movableBench, slot);
        if (replacement) {
          usedBenchIds.add(replacement.id);
          const priority = isSevere ? 100 : isDoubtful ? 85 : 75;
          moves.push({
            id: `${snap.leagueId}-${starter.id}-injury`,
            leagueName,
            type: 'injury',
            starterId: starter.id,
            replacementId: replacement.id,
            outPlayer: movePlayer(starter),
            inPlayer: movePlayer(replacement),
            priority,
            pointsGain: replacement.points,
            line: `Sit ${starter.name} → start ${replacement.name}`,
            why: `${starter.name} is ${injuryStatus}; ${replacement.name} projects ${formatMovePoints(replacement.points)} pts`,
          });
        }
        return;
      }

      if (starter.isLocked) return;

      const isFlex = FLEX_SLOTS.has(slot);
      const minGain = isFlex ? 1.5 : 2;
      const upgrade = findBestBenchForSlot(movableBench, slot);
      if (upgrade && upgrade.points - starter.points >= minGain) {
        usedBenchIds.add(upgrade.id);
        const gain = Math.round((upgrade.points - starter.points) * 10) / 10;
        const prefix = isFlex ? 'Flex: sit' : 'Start';
        const line = isFlex
          ? `${prefix} ${starter.name} for ${upgrade.name}`
          : `Sit ${starter.name} → start ${upgrade.name}`;
        moves.push({
          id: `${snap.leagueId}-${starter.id}-upgrade`,
          leagueName,
          type: isFlex ? 'flex' : 'start',
          starterId: starter.id,
          replacementId: upgrade.id,
          outPlayer: movePlayer(starter),
          inPlayer: movePlayer(upgrade),
          priority: 50 + gain,
          pointsGain: gain,
          line,
          why: `${upgrade.name} projects ${formatMovePoints(upgrade.points)} vs ${starter.name} ${formatMovePoints(starter.points)} (+${gain})`,
        });
      }
    });
  });

  moves.sort((a, b) => b.priority - a.priority || b.pointsGain - a.pointsGain);

  const seen = new Set();
  const unique = [];
  let nonInjuryCount = 0;
  for (const move of moves) {
    if (seen.has(move.id)) continue;
    if (move.type !== 'injury') {
      if (nonInjuryCount >= limit) continue;
      nonInjuryCount += 1;
    }
    seen.add(move.id);
    unique.push(move);
  }

  return unique;
};

/**
 * Load the full multi-league week dashboard.
 *
 * @param {object} options
 * @param {string} options.username - Sleeper username
 * @param {number} options.week - NFL week
 * @param {string[]} [options.leagueIds] - Optional subset; defaults to all discovered leagues
 */
export const fetchWeekDashboard = async ({ username, week, leagueIds }) => {
  const nflState = await fetchNflState();
  const season = Number(nflState.league_season || nflState.season || new Date().getFullYear());

  const { userId, leagues } = await discoverUserLeagues(username, season);
  if (!leagues.length) {
    throw new Error(`No ${season} NFL leagues found for ${username}.`);
  }

  const selectedIds = leagueIds?.length
    ? leagueIds
    : leagues.map((l) => l.league_id);

  const leagueMeta = new Map(leagues.map((l) => [l.league_id, l]));

  const [playersDb, { weeklyFor }, scheduleGames] = await Promise.all([
    getSleeperPlayers(),
    buildProjectionLookup(),
    fetchNflSchedule(season).catch(() => []),
  ]);
  const scheduleIndex = buildScheduleIndex(scheduleGames);

  const snapshots = await Promise.all(
    selectedIds.map((leagueId) =>
      fetchLeagueWeekSnapshot({
        leagueId,
        week,
        userId,
        playersDb,
        weeklyFor,
        scheduleIndex,
      }).then((snap) => {
        if (!snap) return null;
        const meta = leagueMeta.get(leagueId);
        return {
          ...snap,
          totalRosters: meta?.total_rosters,
          status: meta?.status,
        };
      })
    )
  );

  const active = snapshots.filter(Boolean);
  if (!active.length) {
    throw new Error(
      `Could not load week ${week} matchups for ${username} in the selected leagues.`
    );
  }

  const rooting = buildMergedRootingBoard(active);
  const rosterMoves = buildRosterMoves(active);

  const allInjuryAlerts = active.flatMap((s) =>
    s.myTeam.starters
      .filter((p) => p.injuryAlert)
      .map((p) => ({ ...p, league: s.leagueName }))
  );

  return {
    username,
    userId,
    season,
    week,
    nflState,
    discoveredLeagues: leagues.map((l) => ({
      leagueId: l.league_id,
      name: l.name,
      totalRosters: l.total_rosters,
      status: l.status,
    })),
    selectedLeagueIds: selectedIds,
    leagues: active,
    rooting,
    rosterMoves,
    injuryAlerts: allInjuryAlerts,
    usingProjections: active.some((s) =>
      [...s.myTeam.starters, ...s.opponent.starters].some((p) => p.pointSource === 'projection')
    ),
  };
};
